import { Box, Dialog, Grid, Typography } from "@mui/material";
import styles from "./ReportsSearchDevotee.module.scss";
import { Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { FormikErrors, useFormik } from "formik";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRequiredInputLabel,
  CustomTextFeild,
} from "../../common/StyledComponents/StyledFields";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import ErrorPopup from "../../common/ErrorPopup";
import {
  apiRequest,
  catchErrorMessageHandler,
} from "../../../infrastructure/backendService";
import apiPath from "../../../config/apiPaths.json";
import toast from "react-hot-toast";
import appStyles from "../../../App.module.scss";
import Pagination from "../../common/pagination/pagination";
import PageLoader from "../../common/PageLoader/PageLoader";

interface searchDevoteeProps {
  open: boolean;
  handleClose: () => void;
  hanldeSelectUser: (username: string) => void;
}

export const textFieldStyles = {
  textField: {
    border: "1px solid rgba(136, 46, 20, 0.4)",
    backgroundColor: "#FFF9E4",
    borderRadius: "8px",
  },
  errorTextField: {
    border: "1px solid #f05802",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFF9E4",
  },
};

// Define form values type
interface FormValues {
  signinName: string;
  firstName: string;
  lastName: string;
  phonenumber: string;
  email: string;
  city: string;
  state: string;
  zipcode: string;
  // Add a general field for custom error messages
  general?: string;
}

// Validation schema
const validationSchema = Yup.object()
  .shape({
    signinName: Yup.string(),
    firstName: Yup.string(),
    lastName: Yup.string(),
    phonenumber: Yup.string(),
    email: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zipcode: Yup.string(),
  })
  .test("at-least-one", "At least one field must be filled", function (value) {
    return Object.values(value).some((val) => val && val.trim() !== "");
  });

const ReportsSearchDevotee = (props: searchDevoteeProps) => {
  const { open, handleClose, hanldeSelectUser } = props;

  const initialValues: FormValues = {
    signinName: "",
    firstName: "",
    lastName: "",
    phonenumber: "",
    email: "",
    city: "",
    state: "",
    zipcode: "",
  };

  const [loading, setLoading] = useState(false);
  const [usersList, setUsersList] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isAPICalled, setIsAPICalled] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const tableHeaders = [
    { id: "username", fieldName: "Sign-In Name" },
    { id: "name", fieldName: "Name" },
    { id: "address", fieldName: " Address" },
    { id: "email", fieldName: "Email" },
    { id: "phoneWork", fieldName: "Phone No" },
  ];

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      searchDataApi(values);
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      if (!Object.values(values).some((val) => val && val.trim() !== "")) {
        errors.general = "At least one field must be filled";
      }
      return errors;
    },
    enableReinitialize: true,
  });

  const searchDataApi = (values: FormValues) => {
    setLoading(true);
    const reqBody = {
      firstname: values?.firstName,
      lastname: values?.lastName,
      email: values?.email,
      username: values?.signinName,
      city: values?.city,
      state: values?.state,
      zipcode: values?.zipcode,
      sortBy: values?.signinName,
      phoneNumber: values?.phonenumber,
      matchOperator: "AND",
    };
    apiRequest({
      method: "POST",
      endpoint: apiPath.serachDevoteeApi,
      body: JSON.stringify(reqBody),
      includeToken: true,
      params: {
        sortBy: values?.signinName,
        page: pageNumber - 1,
        size: 20,
        direction: "asc",
      },
    })
      .then((res) => {
        setUsersList(res?.data?.content);
        setTotalRecords(res?.data?.totalElements);
        setIsAPICalled(true);
        setLoading(false);
        setPageLoading(false);
      })
      .catch((err) => {
        setIsAPICalled(true);
        setLoading(false);
        setPageLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  useEffect(() => {
    if (isAPICalled) {
      setPageLoading(true);
      searchDataApi(formik.values);
    }
  }, [pageNumber]);

  const getDevoteeAddress = (data: any, details = true) => {
    const { state, city, address, zipcode } = data;
    return [address, city, `${state}${zipcode ? ` ${zipcode}` : " "}`]
      ?.filter(Boolean)
      ?.join(", ");
  };

  const handleBack = () => {
    setTotalRecords(0);
    setIsAPICalled(false);
    setUsersList(null);
  };

  const renderSearchDevoteeForm = () => {
    return (
      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
          autoComplete="off"
        >
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item xs={12} sm={4}>
              {CustomRequiredInputLabel("Sign-In Name", false)}
              <CustomTextFeild
                fullWidth
                value={formik.values.signinName}
                onChange={formik.handleChange}
                name="signinName"
                placeholder="Enter sign-in name"
                className='text_field compact_fields'
              />
              {formik.touched.signinName && formik?.errors?.signinName ? (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.signinName}
                </Typography>
              ) : (
                ""
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {CustomRequiredInputLabel("First Name", false)}
              <CustomTextFeild
                fullWidth
                value={formik.values.firstName}
                onChange={formik.handleChange}
                name="firstName"
                placeholder="Enter first name"
                className='text_field compact_fields '
              />
              {formik.touched.firstName && formik?.errors?.firstName ? (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.firstName}
                </Typography>
              ) : (
                ""
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {CustomRequiredInputLabel("Last Name", false)}
              <CustomTextFeild
                fullWidth
                value={formik.values.lastName}
                onChange={formik.handleChange}
                name="lastName"
                placeholder="Enter last name"
                className='text_field compact_fields '
              />
              {formik.touched.lastName && formik?.errors?.lastName ? (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.lastName}
                </Typography>
              ) : (
                ""
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {CustomRequiredInputLabel("Zip Code", false)}
              <CustomTextFeild
                fullWidth
                value={formik.values.zipcode}
                onChange={formik.handleChange}
                name="zipcode"
                placeholder="Enter zip code"
                className='text_field compact_fields '
              />
              {formik.touched.zipcode && formik?.errors?.zipcode ? (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.zipcode}
                </Typography>
              ) : (
                ""
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {CustomRequiredInputLabel("City", false)}
              <CustomTextFeild
                fullWidth
                value={formik.values.city}
                onChange={formik.handleChange}
                name="city"
                placeholder="Enter city"
                className='text_field compact_fields '
              />
              {formik.touched.city && formik?.errors?.city ? (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.city}
                </Typography>
              ) : (
                ""
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {CustomRequiredInputLabel("State", false)}
              <CustomTextFeild
                fullWidth
                value={formik.values.state}
                onChange={formik.handleChange}
                name="state"
                placeholder="Enter state"
                className='text_field compact_fields '
              />
              {formik.touched.state && formik?.errors?.state ? (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.state}
                </Typography>
              ) : (
                ""
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {CustomRequiredInputLabel("Phone Number", false)}
              <CustomTextFeild
                fullWidth
                value={formik.values.phonenumber}
                onChange={formik.handleChange}
                name="phonenumber"
                placeholder="Enter phone number"
                className='text_field compact_fields '
              />
              {formik.touched.phonenumber && formik?.errors?.phonenumber ? (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.phonenumber}
                </Typography>
              ) : (
                ""
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {CustomRequiredInputLabel("Email ID", false)}
              <CustomTextFeild
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                placeholder="Enter email"
                className='text_field compact_fields '
              />
              {formik.touched.email && formik?.errors?.email ? (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.email}
                </Typography>
              ) : (
                ""
              )}
            </Grid>
          </Grid>
          <Box mt="15px">
            <CommonButton
              onClick={(e) => {
                e.preventDefault();
                formik.handleSubmit();
              }}
              sx={{ mr: "10px" }}
              type="submit"
              disabled={!(formik.isValid && formik.dirty)}
            >
              <ButtonLoader isBtnLoading={loading} btnText={"Search"} />
            </CommonButton>
            <CommonOutlinedButton onClick={handleClose}>
              <ButtonLoader isBtnLoading={false} btnText={"Cancel"} />
            </CommonOutlinedButton>
          </Box>
        </form>
        {isAPICalled && !(totalRecords > 0) && (
          <Typography
            sx={{
              margin: "10px 0 0",
              color: "red",
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            No Records Found
          </Typography>
        )}
      </Box>
    );
  };

  const renderUserDetailsTable = () => {
    return (
      <Box>
        <Typography
          className="sub_heading"
          sx={{ mb: "5px", mt: "0px !important" }}
        >
          Search Results
        </Typography>
        <Box
          className="print_width_height"
          sx={{
            maxHeight: { xs: "calc(100vh - 230px)", sm: "calc(100vh - 255px)" },
            minHeight: { xs: "150px", sm: "auto" },
            overflow: "auto",
            position: "relative",
            minWidth: { xs: "100%", md: "500px" },
            maxWidth: { xs: "100%" },
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#F2BE22", // Scrollbar color
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "white",
            },
          }}
        >
          <table
            className={appStyles.table_common_container}
            style={{ position: "relative" }}
          >
            <thead style={{ position: "sticky", top: "0px" }}>
              {tableHeaders?.map((each: any) => {
                if (each?.id === "startTime" || each?.id === "endTime") {
                  return <th className={styles.no_wrap}>{each?.fieldName}</th>;
                } else {
                  return <th>{each?.fieldName}</th>;
                }
              })}
            </thead>
            <tbody>
              {usersList?.map?.((row: any, index: number) => (
                <tr key={index}>
                  <td className={appStyles.no_wrap}>
                    <Typography onClick={() => hanldeSelectUser(row?.username)}>
                      {row?.username}
                    </Typography>
                  </td>
                  <td className={appStyles.no_wrap}>
                    {row?.firstname} {row?.lastname}
                  </td>
                  <td>{getDevoteeAddress(row)}</td>
                  <td className={appStyles.no_wrap}>{row?.email}</td>
                  <td className={appStyles.no_wrap}>{row?.phoneWork}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: "15px",
          }}
        >
          <CommonButton onClick={handleBack}>Back</CommonButton>
          {totalRecords > 20 && (
            <Pagination
              items={totalRecords}
              itemsPerPage={20}
              setPageNumber={setPageNumber}
              pageNumber={pageNumber}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "850px" },
          maxWidth: { xs: "100%", sm: "850px" },
          margin: { xs: "10px", sm: "20px" },
        },
      }}
    >
      <Box
        className={styles.search_devotee_main_container}
        sx={{
          width: "100%",
        }}
      >
        <Box className={styles.dialog_title_container}>
          <Typography
            className={"main_heading"}
            sx={{ color: "white !important" }}
          >
            Search Devotee
          </Typography>
          <Close
            sx={{ color: "white", cursor: "pointer" }}
            onClick={handleClose}
          />
        </Box>
        <Box className={styles.dialog_inner_container}>
          {!(usersList?.length > 0)
            ? renderSearchDevoteeForm()
            : renderUserDetailsTable()}
        </Box>
      </Box>
      {pageLoading && <PageLoader loading={pageLoading} />}
    </Dialog>
  );
};

export default ReportsSearchDevotee;
